import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import api from "../../../api/api";
import { useAuth } from "../../../features/auth/AuthContext";
import { delete_icon } from '../../../../src/assets/images/images.ts'

const MAX_LINKS = 3;

/* ----------------------------- helpers ----------------------------- */

// validate a pasted URL — YouTube only
function parseLinkUrl(rawUrl) {
  const url = (rawUrl || "").trim();
  if (!url) return { valid: false, error: "Please paste a URL." };

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return { valid: false, error: "That doesn't look like a valid URL." };
  }

  const host = parsed.hostname.replace(/^www\./, "");
  const isYoutube =
    host === "youtube.com" || host === "m.youtube.com" || host === "youtu.be";

  if (!isYoutube) {
    return { valid: false, error: "Please paste a valid YouTube link." };
  }

  // let videoId = "";
  // if (host === "youtu.be") {
  //   videoId = parsed.pathname.slice(1);                      // youtu.be/ABC123
  // } else if (parsed.pathname.startsWith("/embed/")) {
  //   videoId = parsed.pathname.split("/embed/")[1];           // youtube.com/embed/ABC123
  // } else if (parsed.pathname.startsWith("/shorts/")) {
  //   videoId = parsed.pathname.split("/shorts/")[1];          // youtube.com/shorts/ABC123
  // } else if (parsed.pathname.startsWith("/live/")) {
  //   videoId = parsed.pathname.split("/live/")[1];            // youtube.com/live/ABC123
  // } else {
  //   videoId = parsed.searchParams.get("v") || "";            // youtube.com/watch?v=ABC123
  // }
  // videoId = (videoId || "").split("/")[0];


  // if (!videoId) {
  //   return { valid: false, error: "Couldn't find a video id in that link." };
  // }

  if (!isYoutube) {
    return {
      valid: false,
      error: "Please paste a valid YouTube link.",
    };
  }

  return { valid: true, type: "youtube", rawUrl: url };
}

let _rowSeq = 0;
const makeLinkRow = (overrides = {}) => ({
  id:
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `link-${Date.now()}-${_rowSeq++}`,
  value: "",
  error: "",
  loading: false, // API in flight → spinner + "Analysing video"
  saved: false, // already stored / just generated → read-only + "Saved"
  locked: false,
  rawUrl: "",
  ...overrides,
});

/* ------------------------------ component ------------------------------ */

const UploadLinkModal = ({ open, onClose, folderId, type, id, onUploadComplete, path }) => {
  const { token } = useAuth();
  const [links, setLinks] = useState([makeLinkRow()]);
  const [fetching, setFetching] = useState(false);

  const currentFolder = path?.at(-1);





  // ---- fetch existing links whenever the modal opens ----
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const fetchExisting = async () => {
      try {
        setFetching(true);
        const { data: res } = await api.get(
          `manual-upload/folder-youtube-links/?folder_id=${folderId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
          },
        );

        if (cancelled) return;

        const existing = Array.isArray(res?.links) ? res.links : [];
        const savedRows = existing.map((l) =>
          makeLinkRow({
            value: l.url,
            rawUrl: l.url,
            saved: true,
            locked: true,
          }),
        );

        // // show saved rows; add one empty row only if we're under the cap
        // const rows =
        //   savedRows.length < MAX_LINKS
        //     ? [...savedRows, makeLinkRow()]
        //     : savedRows;

        // setLinks(rows.length ? rows : [makeLinkRow()]);

        const rows = savedRows;

        setLinks(rows.length ? rows : [makeLinkRow()]);

        setLinks(rows.length ? rows : [makeLinkRow()]);
      } catch (err) {
        console.error("folder-youtube-links fetch failed:", err);
        if (!cancelled) setLinks([makeLinkRow()]);
      } finally {
        if (!cancelled) setFetching(false);
      }
    };

    fetchExisting();
    return () => {
      cancelled = true;
    };
  }, [open, folderId, token]);

  /* --------------------------- row handlers --------------------------- */

  const updateRow = (id, patch) =>
    setLinks((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const handleChange = (id, value) =>
    updateRow(id, { value, error: "", saved: false });

  const handleAddLink = () => {
    setLinks((prev) => {
      if (prev.length >= MAX_LINKS) {
        toast.error(`You can add up to ${MAX_LINKS} links.`);
        return prev;
      }
      return [...prev, makeLinkRow()];
    });
  };

  // collect URLs already saved (existing + previously generated this session)
  const savedUrls = () =>
    links.filter((r) => r.saved && (r.rawUrl || r.value)).map((r) => r.rawUrl || r.value);

  const handleGenerate = async (id) => {
    const row = links.find((r) => r.id === id);
    if (!row) return;

    const result = parseLinkUrl(row.value);
    if (!result.valid) {
      updateRow(id, { error: result.error });
      return;
    }

    try {
      updateRow(id, { loading: true, error: "", locked: true });

      const { data: res } = await api.post(
        "/courses/yt-transcribe/",
        {
          folder_id: folderId,
          youtube_url: result.rawUrl, // 👈 only the URL this row entered
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        },
      );

      updateRow(id, {
        loading: false,
        saved: true,
        locked: true,
        rawUrl: result.rawUrl,
        error: "",
      });
    } catch (err) {
      console.error("youtube-transcript failed:", err);
      updateRow(id, {
        loading: false,
        locked: false,
        error: "Couldn't analyse this link. Please try again.",
      });
    }
    onUploadComplete?.();
  };

  const handleRemove = async (rowId) => {
    const row = links.find((r) => r.id === rowId);
    const urlToDelete = row?.rawUrl || row?.value;
    const wasSaved = !!(row?.saved && urlToDelete);

    if (wasSaved) {
      try {
        updateRow(rowId, { deleting: true });

        await api.post(
          "/youtube-url/delete/",
          {
            entity_type: path.length <= 2 ? "course" : "lesson",
            entity_id: currentFolder?.entity_id ?? null,
            youtube_url: urlToDelete,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
          },
        );
      } catch (err) {
        console.error("youtube-url delete failed:", err);
        toast.error("Couldn't remove this link. Please try again.");
        updateRow(rowId, { deleting: false });
        return;
      }
    }

    // reset row to fresh empty state (keep same id)
    setLinks((prev) =>
      prev.map((r) =>
        r.id === rowId ? { ...makeLinkRow(), id: r.id } : r,
      ),
    );

    // toast only when a saved link was actually deleted
    if (wasSaved) {
      toast.success("Link removed successfully.");
    }

    onUploadComplete?.();
  };


  const handleClose = () => {
    // reset so a fresh open re-fetches cleanly
    setLinks([makeLinkRow()]);
    onClose?.();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40"
      onClick={handleClose}
    >
      <div
        className="w-[92%] max-w-[560px] bg-white rounded-[16px] p-6 shadow-[0_12px_40px_rgba(0,0,0,0.18)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between">
          <h3 className="m-0 text-[18px] font-bold text-[#1f1f1f]">
            Upload Link
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="bg-transparent border-none text-[16px] text-[#555] cursor-pointer"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <p className="mt-4 mb-3 text-[15px] font-semibold text-[#2b2b2b]">
          Drop YouTube Link
        </p>

        {fetching ? (
          <div className="flex items-center gap-2 text-[#0068FF] text-[0.9rem] py-3">
            <span className="inline-block w-[16px] h-[16px] border-2 border-[#0068FF] border-t-transparent rounded-full animate-spin" />
            Loading links...
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {links.map((row) => {
                const isLocked = row.loading || row.saved || row.locked;
                return (
                  <div key={row.id} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex items-center gap-2 flex-1 border rounded-[8px] px-3 py-2 ${row.error ? "border-[#e53935]" : "border-[#d9d9d9]"
                          } ${isLocked ? "bg-[#FAFAFA]" : ""}`}
                      >
                        <span className="text-[#8a8a8a] flex-shrink-0">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                          >
                            <circle cx="12" cy="12" r="9" />
                            <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
                          </svg>
                        </span>

                        <input
                          type="text"
                          value={row.value}
                          placeholder="Paste Your URL here..."
                          readOnly={isLocked}
                          onChange={(e) => handleChange(row.id, e.target.value)}
                          onKeyDown={(e) =>
                            e.key === "Enter" &&
                            !isLocked &&
                            handleGenerate(row.id)
                          }
                          className={`flex-1 bg-transparent outline-none text-[0.9rem] text-[#222] border-none ${isLocked ? "cursor-default" : ""
                            }`}
                        />

                        {row.loading ? (
                          <span className="flex items-center gap-1.5 text-[#0068FF] text-[0.85rem] flex-shrink-0">
                            <span className="inline-block w-[14px] h-[14px] border-2 border-[#0068FF] border-t-transparent rounded-full animate-spin" />
                            Analysing video
                          </span>
                        ) : row.saved ? (
                          <span className="flex items-center gap-1 text-[#1aa251] text-[0.85rem] font-medium flex-shrink-0">
                            <i className="fa-solid fa-check" /> Saved
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleGenerate(row.id)}
                            disabled={!row.value.trim()}
                            className={`text-[0.9rem] font-medium flex-shrink-0 bg-transparent border-none ${!row.value.trim()
                              ? "text-[#c2c2c2] cursor-not-allowed"
                              : "text-[#0068FF] cursor-pointer"
                              }`}
                          >
                            Generate
                          </button>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemove(row.id)}
                        disabled={row.deleting}
                        className="text-[#888] hover:text-[#e53935] flex-shrink-0 px-1 bg-transparent border-none cursor-pointer disabled:cursor-not-allowed"
                        aria-label="Remove link"
                      >
                        {row.deleting ? (
                          <span className="inline-block w-[14px] h-[14px] border-2 border-[#888] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          // "✕"
                          <img src={delete_icon} />
                        )}
                      </button>
                    </div>

                    {row.error && (
                      <p className="text-[#e53935] text-[0.75rem] pl-1">
                        {row.error}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* {links.length < MAX_LINKS && (
              <button
                type="button"
                onClick={handleAddLink}
                className="flex items-center gap-1 mt-2 text-[#0068FF] text-[0.85rem] font-medium bg-transparent border-none cursor-pointer"
              >
                <span className="text-[1.1rem] leading-none">+</span> Add Link
              </button>
            )} */}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default UploadLinkModal;